import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import * as XLSX from "xlsx";

const MonthlyCashChequeReport = () => {
  const today = new Date();

  const [currentSession, setCurrentSession] = useState(null);
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [students, setStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedClass, setSelectedClass] = useState("all");
  const [selectedSection, setSelectedSection] = useState("all");
  const [selectedMonth, setSelectedMonth] = useState(today.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(today.getFullYear());

  const months = [
    "January","February","March","April","May","June",
    "July","August","September","October","November","December"
  ];

  // ✅ Load session
  useEffect(() => {
    const sessionId = localStorage.getItem("currentSessionId");
    if (!sessionId) return console.error("Session not found in localStorage");
    setCurrentSession(sessionId);
  }, []);

  // Fetch classes
  useEffect(() => {
    if (!currentSession) return;
    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/api/classes/getClasses?sessionId=${currentSession}`)
      .then(res => setClasses(res.data))
      .catch(console.error);
  }, [currentSession]);

  // Fetch sections dynamically if needed
  useEffect(() => {
    if (!currentSession || selectedClass === "all") return;
   
    axios.get(
      `${process.env.REACT_APP_API_BASE_URL}/api/sections/${encodeURIComponent(selectedClass)}/getSectionsByClassName`,
      { params: { sessionId: currentSession } }
    ).then(res => setSections(res.data || []))
     .catch(console.error);
  }, [selectedClass, currentSession]);
  // Fetch students
  useEffect(() => {
    if (!currentSession) return;

    axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/fees/report/monthly-cash-cheque-report`, {
      params: {
        sessionId: currentSession,
        year: selectedYear,
        month: selectedMonth,
        classId: selectedClass !== "all" ? selectedClass : undefined,
        sectionId: selectedSection !== "all" ? selectedSection : undefined,
      }
    })
    .then(res => setStudents(res.data || []))
    .catch(console.error);
  }, [currentSession, selectedClass, selectedSection, selectedMonth, selectedYear]);

  // Filter students by search
  const filteredStudents = useMemo(() => {
    return students.filter(s => 
      `${s.firstName} ${s.lastName || ""}`.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [students, searchTerm]);

  // Totals
  const totalPaid = useMemo(() => filteredStudents.reduce((sum, s) => sum + (s.paidAmount || 0), 0), [filteredStudents]);
  const totalCash = useMemo(() => filteredStudents.reduce((sum, s) => sum + (s.cashAmount || 0), 0), [filteredStudents]);
  const totalCheque = useMemo(() => filteredStudents.reduce((sum, s) => sum + (s.chequeAmount || 0), 0), [filteredStudents]);

  // Export
  const handleExport = () => {
    if (!filteredStudents.length) return;

    const exportData = filteredStudents.map((s, i) => ({
      "Sr No": i + 1,
      "Student Name": `${s.firstName} ${s.lastName || ""}`,
      "Class": s.className || "N/A",
      "Section": s.section || "N/A",
      "Paid Amount": s.paidAmount,
      "Cash": s.cashAmount,
      "Cheque": s.chequeAmount,
      "Balance": s.balance,
      "Payment Date": s.date || "-"
    }));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportData);
    XLSX.utils.book_append_sheet(wb, ws, "Monthly Fee Report");
    XLSX.writeFile(wb, `Monthly_Fee_Report_${selectedMonth}_${selectedYear}.xlsx`);
  };

  return (
    <div className="min-h-screen p-6 bg-gray-50">
      <div className="max-w-7xl mx-auto bg-white shadow-lg rounded-lg p-6">
        <h2 className="text-2xl font-bold mb-6 text-gray-700">Monthly Cash & Cheque Report</h2>

        {/* Summary */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="bg-green-100 p-4 rounded">
            <h4 className="text-green-700 font-semibold">Total Paid</h4>
            <p className="text-2xl font-bold text-green-800">₹{totalPaid}</p>
          </div>
          <div className="bg-blue-100 p-4 rounded">
            <h4 className="text-blue-700 font-semibold">Cash Collected</h4>
            <p className="text-2xl font-bold text-blue-800">₹{totalCash}</p>
          </div>
          <div className="bg-yellow-100 p-4 rounded">
            <h4 className="text-yellow-700 font-semibold">Cheque Collected</h4>
            <p className="text-2xl font-bold text-yellow-800">₹{totalCheque}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          <select value={selectedMonth} onChange={e => setSelectedMonth(+e.target.value)} className="border rounded px-3 py-2">
            {months.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>

          <select value={selectedYear} onChange={e => setSelectedYear(+e.target.value)} className="border rounded px-3 py-2">
            <option>{today.getFullYear()}</option>
            <option>{today.getFullYear() - 1}</option>
          </select>

          <select value={selectedClass} onChange={e => setSelectedClass(e.target.value)} className="border rounded px-3 py-2">
            <option value="all">All Classes</option>
            {classes.map(c => <option key={c._id} value={c.className}>{c.className}</option>)}
          </select>

          <select value={selectedSection} onChange={e => setSelectedSection(e.target.value)} className="border rounded px-3 py-2">
            <option value="all">All Sections</option>
            {sections.map(s => <option key={s._id} value={s.sectionName}>{s.sectionName}</option>)}
          </select>

          <input
            type="text"
            placeholder="Search student..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="border rounded px-3 py-2"
          />
        </div>

        {/* Export */}
        <div className="flex justify-end mb-4">
          <button
            onClick={handleExport}
            disabled={!filteredStudents.length}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
          >
            Export to Excel
          </button>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full border text-center">
            <thead className="bg-gray-100">
              <tr>
                <th className="border px-4 py-2">Sr</th>
                <th className="border px-4 py-2">Student Name</th>
                <th className="border px-4 py-2">Class</th>
                <th className="border px-4 py-2">Section</th>
                <th className="border px-4 py-2">Paid Amount</th>
                <th className="border px-4 py-2">Cash</th>
                <th className="border px-4 py-2">Cheque</th>
                <th className="border px-4 py-2">Balance</th>
                <th className="border px-4 py-2">Payment Date</th>
              </tr>
            </thead>
            <tbody>
              {filteredStudents.length ? filteredStudents.map((s, i) => (
                <tr key={s.studentId}>
                  <td className="border px-4 py-2">{i + 1}</td>
                  <td className="border px-4 py-2">{s.firstName} {s.lastName}</td>
                  <td className="border px-4 py-2">{s.className}</td>
                  <td className="border px-4 py-2">{s.section}</td>
                  <td className="border px-4 py-2 text-green-700 font-semibold">₹{s.paidAmount}</td>
                  <td className="border px-4 py-2">₹{s.cashAmount}</td>
                  <td className="border px-4 py-2">₹{s.chequeAmount}</td>
                  <td className="border px-4 py-2">₹{s.balance}</td>
                  <td className="border px-4 py-2">{s.date || "—"}</td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="9" className="py-6 text-gray-500">No records found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MonthlyCashChequeReport;